import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, idr, openDocument, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChangePasswordForm } from "@/pages/AuthPages";
import { toast } from "sonner";
import { FileText, Upload, LogOut, KeyRound } from "lucide-react";

const STATUS = { sent: "Terkirim", accepted: "Disetujui", rejected: "Ditolak", expired: "Kedaluwarsa", invoiced: "Sudah diinvoice", issued: "Terbit", paid: "Lunas" };

function DocTable({ title, rows, kind, onUpload }) {
  return (
    <div className="border border-border bg-card rounded-sm" data-testid={`portal-${kind}`}>
      <div className="px-5 py-3 border-b border-border font-semibold">{title}</div>
      {rows.length === 0 ? <p className="p-5 text-sm text-muted-foreground">Belum ada dokumen.</p> : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`portal-${kind}-row`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-mono text-sm">{r.quote_number || r.invoice_number}</div>
                <div className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)} · {STATUS[r.status] || r.status}</div>
              </div>
              <div className="font-mono text-sm">{idr(r.total)}</div>
              <Button size="sm" variant="outline" data-testid={`portal-${kind}-pdf`}
                onClick={() => openDocument(`/portal/${kind}/${r.id}/pdf`, `${r.quote_number || r.invoice_number}.pdf`).catch(() => toast.error("Gagal membuka PDF"))}>
                <FileText className="w-4 h-4 mr-1" /> PDF</Button>
              {onUpload && r.status === "sent" && (
                <Button size="sm" onClick={() => onUpload(r)} data-testid="portal-upload-po-btn"><Upload className="w-4 h-4 mr-1" /> Kirim PO</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState({ quotations: [], invoices: [] });
  const [showPw, setShowPw] = useState(false);
  const fileRef = useRef(null);
  const target = useRef(null);
  const load = () => api.get("/portal/documents").then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat dokumen"));
  useEffect(() => { load(); }, []);
  const pick = (q) => { target.current = q; fileRef.current?.click(); };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !target.current) return;
    const fd = new FormData(); fd.append("file", file);
    const t = toast.loading("Membaca PO dengan AI…");
    try {
      await api.post(`/portal/quotations/${target.current.id}/po`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PO diterima. Tim kami segera memproses pesanan Anda.", { id: t }); load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Gagal mengirim PO", { id: t }); }
  };
  return (
    <div className="min-h-screen bg-secondary/40" data-testid="portal-page">
      <header className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/grcom-logo.png" alt="CV GRcom" className="h-9" />
          <div className="flex-1 text-sm"><div className="font-semibold">Portal Customer</div><div className="text-xs text-muted-foreground">{user?.name} · {user?.email}</div></div>
          <Button size="sm" variant="outline" onClick={() => setShowPw(!showPw)} data-testid="portal-change-password-btn"><KeyRound className="w-4 h-4 mr-1" /> Ganti Password</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); navigate("/login"); }} data-testid="portal-logout-btn"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        {showPw && <div className="bg-card border border-border rounded-sm p-5"><ChangePasswordForm /></div>}
        <DocTable title="Penawaran" rows={d.quotations} kind="quotations" onUpload={pick} />
        <DocTable title="Invoice" rows={d.invoices} kind="invoices" />
        <input ref={fileRef} type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="portal-po-input" />
      </main>
    </div>
  );
}
